import React, { useMemo, useState } from 'react';
import {
  TimeRangePreset,
  ComparisonPeriod as ComparisonPeriodType,
  computePercentageChange,
  getTrendDirection,
  isIncreaseConcerning,
  getTimeRange,
} from '../../utils/timezone';
import { ThresholdConfig, fmtFreqLabel, fmtFreqUnit } from './behaviorTypes';

// ─── Types ──────────────────────────────────────────────────────────────────────

// Format duration in minutes to human-readable string
export function formatDuration(minutes: number): string {
  if (minutes < 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(minutes / 1440);
  const remainingMinutes = minutes % 1440;
  const hours = Math.floor(remainingMinutes / 60);
  const mins = Math.round(remainingMinutes % 60);
  if (mins > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${days}d ${hours}h`;
  return `${days}d`;
}

export interface BehaviorStats {
  scratchCount: number;
  walkCount: number;
  lyingDuration: number; // minutes
  sittingDuration: number; // minutes
  standingDuration: number; // minutes
  lyingCount: number;
  sittingCount: number;
  standingCount: number;
  totalEvents: number;
}

export type ComparisonMode = 'threshold' | 'trend';
export type SummaryView = 'frequency' | 'duration';

interface BehaviorSummaryProps {
  /** Current period stats derived from real DB data */
  stats: BehaviorStats;
  /** Previous period stats for trend comparison (null = no comparison data) */
  previousStats?: BehaviorStats | null;
  /** Threshold configuration for alert mode */
  thresholds?: ThresholdConfig;
  /** Currently selected time range preset */
  timeRange: TimeRangePreset;
  /** Currently selected timezone */
  timezone: string;
  /** Currently selected comparison mode */
  comparisonMode: ComparisonMode;
  /** Currently selected comparison period (for trend mode) */
  comparisonPeriod: ComparisonPeriodType['key'];
  /** Currently selected summary view (frequency or duration) */
  summaryView: SummaryView;
  /** Callbacks for state changes */
  onTimeRangeChange: (preset: TimeRangePreset) => void;
  onComparisonModeChange: (mode: ComparisonMode) => void;
  onComparisonPeriodChange: (period: ComparisonPeriodType['key']) => void;
  onSummaryViewChange: (view: SummaryView) => void;
  /** Last data refresh timestamp (epoch ms) */
  lastUpdated?: number;
  /** Whether data is currently being fetched */
  isLoading?: boolean;
}

// ─── Stat Card Component ────────────────────────────────────────────────────────

/** A single threshold to display on a stat card */
export interface ThresholdDisplay {
  /** Human-readable threshold label, e.g. "30/hr" or "45min" */
  label: string;
  /** Whether this specific threshold has been exceeded */
  exceeded: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  unit?: string;
  /** If 'duration', value will be formatted using formatDuration() */
  valueType?: 'count' | 'duration';
  alert?: boolean;
  /** Replaced single `threshold` string with structured thresholds array */
  thresholds?: ThresholdDisplay[];
  trend?: {
    percentageChange: number | null;
    direction: 'up' | 'down' | 'neutral';
    concerning: boolean;
  };
  mode: ComparisonMode;
  disabled?: boolean;
  disabledTooltip?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  unit,
  valueType = 'count',
  alert,
  thresholds,
  trend,
  mode,
  disabled = false,
  disabledTooltip,
}) => {
  // Format value if it's a duration type
  const displayValue = useMemo(() => {
    if (typeof value === 'string') return value;
    if (valueType === 'duration') {
      return formatDuration(value);
    }
    return Number.isInteger(value) ? value : (value as number).toFixed(1);
  }, [value, valueType]);
  if (disabled) {
    return (
      <div className="relative p-4 rounded-lg bg-gray-50 dark:bg-claude-darkBg opacity-50 cursor-not-allowed group">
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">N/A</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400 dark:text-gray-500">{label}</span>
        </div>
        {disabledTooltip && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            {disabledTooltip}
          </div>
        )}
      </div>
    );
  }

  const isAlert = mode === 'threshold' && alert;
  const isTrendConcerning = mode === 'trend' && trend?.concerning;
  const isTrendPositive = mode === 'trend' && trend && !trend.concerning && trend.direction !== 'neutral';

  const bgClass = isAlert
    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
    : isTrendConcerning
      ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
      : isTrendPositive
        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
        : 'bg-gray-50 dark:bg-claude-darkBg';

  return (
    <div className={`p-4 rounded-lg ${bgClass}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl font-bold text-gray-900 dark:text-claude-darkText">
          {displayValue}
        </span>
        {unit && <span className="text-sm text-gray-400">{unit}</span>}
        {mode === 'threshold' && isAlert && (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
        {mode === 'trend' && trend && trend.direction !== 'neutral' && (
          <span className={`text-sm font-semibold ${
            trend.concerning ? 'text-red-500' : 'text-green-500'
          }`}>
            {trend.direction === 'up' ? '↑' : '↓'}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        {mode === 'threshold' && thresholds && thresholds.length > 0 && (
          <span className={`${isAlert ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400'}`}>
            {thresholds.map((t, i) => {
              const prefix = t.exceeded ? '>' : '≤';
              return `${i > 0 ? ' | ' : ''}${prefix}${t.label}`;
            })}
          </span>
        )}
        {mode === 'trend' && trend && trend.percentageChange !== null && (
          <span className={`font-medium ${
            trend.concerning ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
          }`}>
            {trend.percentageChange > 0 ? '+' : ''}{trend.percentageChange}%
          </span>
        )}
      </div>
    </div>
  );
};

// ─── BehaviorSummary Component ──────────────────────────────────────────────────

export type BehaviorTab = 'active' | 'comingSoon';

const BehaviorSummary: React.FC<BehaviorSummaryProps> = ({
  stats,
  previousStats,
  thresholds,
  timeRange,
  timezone,
  comparisonMode,
  comparisonPeriod,
  summaryView,
  onTimeRangeChange,
  onComparisonModeChange,
  onComparisonPeriodChange,
  onSummaryViewChange,
  lastUpdated,
  isLoading = false,
}) => {
  // lastUpdated is available for future use (e.g., showing "last updated" timestamp)
  void lastUpdated;
  // These props are retained for interface stability but the TimeRangeSelector
  // has moved to the parent SequenceDetectView for shared control.
  void onTimeRangeChange;
  void comparisonPeriod;
  void onComparisonPeriodChange;
  const [activeTab, setActiveTab] = useState<BehaviorTab>('active');
  const hasRealData = stats.totalEvents > 0;

  // Compute trend data for each stat
  const trendData = useMemo(() => {
    if (comparisonMode !== 'trend' || !previousStats) return null;

    return {
      scratchCount: {
        percentageChange: computePercentageChange(stats.scratchCount, previousStats.scratchCount),
        direction: getTrendDirection(computePercentageChange(stats.scratchCount, previousStats.scratchCount)),
        concerning: isIncreaseConcerning('scratchCount') && getTrendDirection(computePercentageChange(stats.scratchCount, previousStats.scratchCount)) === 'up',
      },
      walkCount: {
        percentageChange: computePercentageChange(stats.walkCount, previousStats.walkCount),
        direction: getTrendDirection(computePercentageChange(stats.walkCount, previousStats.walkCount)),
        concerning: false, // walk count changes are neutral
      },
      lyingDuration: {
        percentageChange: computePercentageChange(stats.lyingDuration, previousStats.lyingDuration),
        direction: getTrendDirection(computePercentageChange(stats.lyingDuration, previousStats.lyingDuration)),
        concerning: isIncreaseConcerning('lyingDuration') && getTrendDirection(computePercentageChange(stats.lyingDuration, previousStats.lyingDuration)) === 'up',
      },
      sittingDuration: {
        percentageChange: computePercentageChange(stats.sittingDuration, previousStats.sittingDuration),
        direction: getTrendDirection(computePercentageChange(stats.sittingDuration, previousStats.sittingDuration)),
        concerning: isIncreaseConcerning('sittingDuration') && getTrendDirection(computePercentageChange(stats.sittingDuration, previousStats.sittingDuration)) === 'up',
      },
      standingDuration: {
        percentageChange: computePercentageChange(stats.standingDuration, previousStats.standingDuration),
        direction: getTrendDirection(computePercentageChange(stats.standingDuration, previousStats.standingDuration)),
        concerning: isIncreaseConcerning('standingDuration') && getTrendDirection(computePercentageChange(stats.standingDuration, previousStats.standingDuration)) === 'up',
      },
      lyingCount: {
        percentageChange: computePercentageChange(stats.lyingCount, previousStats.lyingCount),
        direction: getTrendDirection(computePercentageChange(stats.lyingCount, previousStats.lyingCount)),
        concerning: isIncreaseConcerning('lyingCount') && getTrendDirection(computePercentageChange(stats.lyingCount, previousStats.lyingCount)) === 'up',
      },
      sittingCount: {
        percentageChange: computePercentageChange(stats.sittingCount, previousStats.sittingCount),
        direction: getTrendDirection(computePercentageChange(stats.sittingCount, previousStats.sittingCount)),
        concerning: isIncreaseConcerning('sittingCount') && getTrendDirection(computePercentageChange(stats.sittingCount, previousStats.sittingCount)) === 'up',
      },
      standingCount: {
        percentageChange: computePercentageChange(stats.standingCount, previousStats.standingCount),
        direction: getTrendDirection(computePercentageChange(stats.standingCount, previousStats.standingCount)),
        concerning: isIncreaseConcerning('standingCount') && getTrendDirection(computePercentageChange(stats.standingCount, previousStats.standingCount)) === 'up',
      },
      totalEvents: {
        percentageChange: computePercentageChange(stats.totalEvents, previousStats.totalEvents),
        direction: getTrendDirection(computePercentageChange(stats.totalEvents, previousStats.totalEvents)),
        concerning: false, // total events changes are neutral
      },
    };
  }, [comparisonMode, stats, previousStats]);

  // Compute alert flags for threshold mode — check warning and critical thresholds
  // A behavior is in "alert" if any threshold (warning or critical) is exceeded
  const isScratchAlert = thresholds?.scratching.enabled && (
    (thresholds.scratching.critical?.frequency != null && stats.scratchCount > thresholds.scratching.critical.frequency) ||
    (thresholds.scratching.warning?.frequency != null && stats.scratchCount > thresholds.scratching.warning.frequency)
  );
  const isWalkingAlert = thresholds?.walking.enabled && (
    (thresholds.walking.critical?.frequency != null && stats.walkCount > thresholds.walking.critical.frequency) ||
    (thresholds.walking.warning?.frequency != null && stats.walkCount > thresholds.walking.warning.frequency)
  );

  // Frequency-specific alert flags (for frequency view)
  const isLyingFreqAlert = thresholds?.lying.enabled && (
    (thresholds.lying.critical?.frequency != null && stats.lyingCount > thresholds.lying.critical.frequency) ||
    (thresholds.lying.warning?.frequency != null && stats.lyingCount > thresholds.lying.warning.frequency)
  );
  const isSittingFreqAlert = thresholds?.sitting.enabled && (
    (thresholds.sitting.critical?.frequency != null && stats.sittingCount > thresholds.sitting.critical.frequency) ||
    (thresholds.sitting.warning?.frequency != null && stats.sittingCount > thresholds.sitting.warning.frequency)
  );
  const isStandingFreqAlert = thresholds?.standing.enabled && (
    (thresholds.standing.critical?.frequency != null && stats.standingCount > thresholds.standing.critical.frequency) ||
    (thresholds.standing.warning?.frequency != null && stats.standingCount > thresholds.standing.warning.frequency)
  );

  // Duration-specific alert flags (for duration view)
  const isLyingDurAlert = thresholds?.lying.enabled && (
    (thresholds.lying.critical?.duration != null && stats.lyingDuration > thresholds.lying.critical.duration) ||
    (thresholds.lying.warning?.duration != null && stats.lyingDuration > thresholds.lying.warning.duration)
  );
  const isSittingDurAlert = thresholds?.sitting.enabled && (
    (thresholds.sitting.critical?.duration != null && stats.sittingDuration > thresholds.sitting.critical.duration) ||
    (thresholds.sitting.warning?.duration != null && stats.sittingDuration > thresholds.sitting.warning.duration)
  );
  const isStandingDurAlert = thresholds?.standing.enabled && (
    (thresholds.standing.critical?.duration != null && stats.standingDuration > thresholds.standing.critical.duration) ||
    (thresholds.standing.warning?.duration != null && stats.standingDuration > thresholds.standing.warning.duration)
  );

  const hasAnyFreqAlert = isScratchAlert || isWalkingAlert || isLyingFreqAlert || isSittingFreqAlert || isStandingFreqAlert;
  const hasAnyDurAlert = isLyingDurAlert || isSittingDurAlert || isStandingDurAlert;

  // Time range label
  const rangeLabel = getTimeRange(timeRange).label;
  // Display timezone directly since it's already in GMT+X format
  const tzDisplay = timezone;

  // Refresh interval text (data refreshes every 30 seconds)
  const refreshText = 'Refreshes every 30s';

  return (
    <div className="rounded-xl dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted p-6 border border-claude-border dark:border-claude-darkBorder">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
            Detected Behaviors
          </h3>
          <div className="flex items-center gap-2">
            {isLoading && (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {refreshText}
            </span>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
            <button
              onClick={() => onComparisonModeChange('threshold')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                comparisonMode === 'threshold'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              ⚠️ Threshold
            </button>
            <button
              onClick={() => onComparisonModeChange('trend')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                comparisonMode === 'trend'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              📈 Trend
            </button>
          </div>

          {/* Summary view toggle: Frequency vs Duration */}
          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
            <button
              onClick={() => onSummaryViewChange('frequency')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                summaryView === 'frequency'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              📊 Frequency
            </button>
            <button
              onClick={() => onSummaryViewChange('duration')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                summaryView === 'duration'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              ⏱️ Duration
            </button>
          </div>

          <span className="text-xs text-gray-400 dark:text-gray-500">
            {rangeLabel} • {tzDisplay}
          </span>

          {/* Tab toggle for stat cards */}
          <div className="flex items-center bg-gray-100 dark:bg-slate-700 rounded-lg p-1 ml-auto">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                activeTab === 'active'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              Active Behaviors
            </button>
            <button
              onClick={() => setActiveTab('comingSoon')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                activeTab === 'comingSoon'
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* Time range selector has been moved to the parent SequenceDetectView
            for shared control across BehaviorSummary, BehaviorFrequencyChart,
            and BehaviorTimelineV2. */}
      </div>

      {/* No data state */}
      {!hasRealData && (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm font-medium">No behavior data available</p>
          <p className="text-xs mt-1">Start a monitoring session to see detected behaviors</p>
        </div>
      )}

      {/* Stat cards — split by tab */}
      {hasRealData && (
        <div className="mb-4">
          {/* Page 1: Active Behaviors (with real data) */}
          {activeTab === 'active' && summaryView === 'frequency' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Walking — frequency */}
              <StatCard
                label="Walking"
                value={stats.walkCount}
                unit={fmtFreqUnit(thresholds?.walking.warning?.frequencyWindow ?? thresholds?.walking.critical?.frequencyWindow)}
                alert={!!isWalkingAlert}
                thresholds={[
                  ...(thresholds?.walking.enabled && thresholds.walking.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.walking.warning.frequency, thresholds.walking.warning.frequencyWindow)}`, exceeded: stats.walkCount > thresholds.walking.warning.frequency }] : []),
                  ...(thresholds?.walking.enabled && thresholds.walking.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.walking.critical.frequency, thresholds.walking.critical.frequencyWindow)}`, exceeded: stats.walkCount > thresholds.walking.critical.frequency }] : []),
                ]}
                trend={trendData?.walkCount ?? undefined}
                mode={comparisonMode}
              />

              {/* Scratching — frequency */}
              <StatCard
                label="Scratching"
                value={stats.scratchCount}
                unit={fmtFreqUnit(thresholds?.scratching.warning?.frequencyWindow ?? thresholds?.scratching.critical?.frequencyWindow)}
                alert={!!isScratchAlert}
                thresholds={[
                  ...(thresholds?.scratching.enabled && thresholds.scratching.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.scratching.warning.frequency, thresholds.scratching.warning.frequencyWindow)}`, exceeded: stats.scratchCount > thresholds.scratching.warning.frequency }] : []),
                  ...(thresholds?.scratching.enabled && thresholds.scratching.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.scratching.critical.frequency, thresholds.scratching.critical.frequencyWindow)}`, exceeded: stats.scratchCount > thresholds.scratching.critical.frequency }] : []),
                ]}
                trend={trendData?.scratchCount ?? undefined}
                mode={comparisonMode}
              />

              {/* Lying — frequency */}
              <StatCard
                label="Lying"
                value={stats.lyingCount}
                unit={fmtFreqUnit(thresholds?.lying.warning?.frequencyWindow ?? thresholds?.lying.critical?.frequencyWindow)}
                alert={!!isLyingFreqAlert}
                thresholds={[
                  ...(thresholds?.lying.enabled && thresholds.lying.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.lying.warning.frequency, thresholds.lying.warning.frequencyWindow)}`, exceeded: stats.lyingCount > thresholds.lying.warning.frequency }] : []),
                  ...(thresholds?.lying.enabled && thresholds.lying.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.lying.critical.frequency, thresholds.lying.critical.frequencyWindow)}`, exceeded: stats.lyingCount > thresholds.lying.critical.frequency }] : []),
                ]}
                trend={trendData?.lyingCount ?? undefined}
                mode={comparisonMode}
              />

              {/* Sitting — frequency */}
              <StatCard
                label="Sitting"
                value={stats.sittingCount}
                unit={fmtFreqUnit(thresholds?.sitting.warning?.frequencyWindow ?? thresholds?.sitting.critical?.frequencyWindow)}
                alert={!!isSittingFreqAlert}
                thresholds={[
                  ...(thresholds?.sitting.enabled && thresholds.sitting.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.sitting.warning.frequency, thresholds.sitting.warning.frequencyWindow)}`, exceeded: stats.sittingCount > thresholds.sitting.warning.frequency }] : []),
                  ...(thresholds?.sitting.enabled && thresholds.sitting.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.sitting.critical.frequency, thresholds.sitting.critical.frequencyWindow)}`, exceeded: stats.sittingCount > thresholds.sitting.critical.frequency }] : []),
                ]}
                trend={trendData?.sittingCount ?? undefined}
                mode={comparisonMode}
              />

              {/* Standing — frequency */}
              <StatCard
                label="Standing"
                value={stats.standingCount}
                unit={fmtFreqUnit(thresholds?.standing.warning?.frequencyWindow ?? thresholds?.standing.critical?.frequencyWindow)}
                alert={!!isStandingFreqAlert}
                thresholds={[
                  ...(thresholds?.standing.enabled && thresholds.standing.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.standing.warning.frequency, thresholds.standing.warning.frequencyWindow)}`, exceeded: stats.standingCount > thresholds.standing.warning.frequency }] : []),
                  ...(thresholds?.standing.enabled && thresholds.standing.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.standing.critical.frequency, thresholds.standing.critical.frequencyWindow)}`, exceeded: stats.standingCount > thresholds.standing.critical.frequency }] : []),
                ]}
                trend={trendData?.standingCount ?? undefined}
                mode={comparisonMode}
              />
            </div>
          )}

          {activeTab === 'active' && summaryView === 'duration' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Lying Duration */}
              <StatCard
                label="Lying"
                value={stats.lyingDuration}
                valueType="duration"
                alert={!!isLyingDurAlert}
                thresholds={[
                  ...(thresholds?.lying.enabled && thresholds.lying.warning?.duration ? [{ label: `⚠${thresholds.lying.warning.duration}min`, exceeded: stats.lyingDuration > thresholds.lying.warning.duration }] : []),
                  ...(thresholds?.lying.enabled && thresholds.lying.critical?.duration ? [{ label: `🔴${thresholds.lying.critical.duration}min`, exceeded: stats.lyingDuration > thresholds.lying.critical.duration }] : []),
                ]}
                trend={trendData?.lyingDuration ?? undefined}
                mode={comparisonMode}
              />

              {/* Sitting Duration */}
              <StatCard
                label="Sitting"
                value={stats.sittingDuration}
                valueType="duration"
                alert={!!isSittingDurAlert}
                thresholds={[
                  ...(thresholds?.sitting.enabled && thresholds.sitting.warning?.duration ? [{ label: `⚠${thresholds.sitting.warning.duration}min`, exceeded: stats.sittingDuration > thresholds.sitting.warning.duration }] : []),
                  ...(thresholds?.sitting.enabled && thresholds.sitting.critical?.duration ? [{ label: `🔴${thresholds.sitting.critical.duration}min`, exceeded: stats.sittingDuration > thresholds.sitting.critical.duration }] : []),
                ]}
                trend={trendData?.sittingDuration ?? undefined}
                mode={comparisonMode}
              />

              {/* Standing Duration */}
              <StatCard
                label="Standing"
                value={stats.standingDuration}
                valueType="duration"
                alert={!!isStandingDurAlert}
                thresholds={[
                  ...(thresholds?.standing.enabled && thresholds.standing.warning?.duration ? [{ label: `⚠${thresholds.standing.warning.duration}min`, exceeded: stats.standingDuration > thresholds.standing.warning.duration }] : []),
                  ...(thresholds?.standing.enabled && thresholds.standing.critical?.duration ? [{ label: `🔴${thresholds.standing.critical.duration}min`, exceeded: stats.standingDuration > thresholds.standing.critical.duration }] : []),
                ]}
                trend={trendData?.standingDuration ?? undefined}
                mode={comparisonMode}
              />
            </div>
          )}

          {/* Page 2: Coming Soon (N/A — requires sequence detection) */}
          {activeTab === 'comingSoon' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* Spin Count — disabled */}
              <StatCard
                label="Spins"
                value={0}
                alert={false}
                thresholds={thresholds?.spin.enabled && (thresholds.spin.warning?.frequency || thresholds.spin.critical?.frequency) ? [
                  ...(thresholds.spin.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.spin.warning.frequency, thresholds.spin.warning.frequencyWindow)}`, exceeded: false }] : []),
                  ...(thresholds.spin.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.spin.critical.frequency, thresholds.spin.critical.frequencyWindow)}`, exceeded: false }] : []),
                ] : undefined}
                mode={comparisonMode}
                disabled
                disabledTooltip="Requires sequence detection (coming soon)"
              />

              {/* Toilet Visits — disabled */}
              <StatCard
                label="Toilet"
                value={0}
                alert={false}
                thresholds={thresholds?.toilet.enabled && (thresholds.toilet.warning?.frequency || thresholds.toilet.critical?.frequency) ? [
                  ...(thresholds.toilet.warning?.frequency ? [{ label: `⚠${fmtFreqLabel(thresholds.toilet.warning.frequency, thresholds.toilet.warning.frequencyWindow)}`, exceeded: false }] : []),
                  ...(thresholds.toilet.critical?.frequency ? [{ label: `🔴${fmtFreqLabel(thresholds.toilet.critical.frequency, thresholds.toilet.critical.frequencyWindow)}`, exceeded: false }] : []),
                ] : undefined}
                mode={comparisonMode}
                disabled
                disabledTooltip="Requires sequence detection (coming soon)"
              />

              {/* Placeholder for alignment */}
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-claude-darkBg opacity-30">
                <div className="text-2xl font-bold text-gray-300 dark:text-gray-600 mb-2">?</div>
                <div className="text-xs text-gray-300 dark:text-gray-600">More coming soon</div>
              </div>

              {/* Placeholder for alignment */}
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-claude-darkBg opacity-30">
                <div className="text-2xl font-bold text-gray-300 dark:text-gray-600 mb-2">?</div>
                <div className="text-xs text-gray-300 dark:text-gray-600">More coming soon</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Alert Summary (threshold mode only) */}
      {comparisonMode === 'threshold' && hasRealData && (summaryView === 'frequency' ? hasAnyFreqAlert : hasAnyDurAlert) && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                Behavioral Anomalies Detected
              </h4>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                {summaryView === 'frequency'
                  ? [
                      isScratchAlert && 'Excessive scratching',
                      isWalkingAlert && 'Excessive walking',
                      isLyingFreqAlert && 'Frequent lying',
                      isSittingFreqAlert && 'Frequent sitting',
                      isStandingFreqAlert && 'Frequent standing',
                    ].filter(Boolean).join(' • ')
                  : [
                      isLyingDurAlert && 'Prolonged lying',
                      isSittingDurAlert && 'Prolonged sitting',
                      isStandingDurAlert && 'Prolonged standing',
                    ].filter(Boolean).join(' • ')
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trend Summary (trend mode only) */}
      {comparisonMode === 'trend' && hasRealData && trendData && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Trend Comparison
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                Comparing current period with previous period.{' '}
                <span className="text-red-500">↑ Red</span> = concerning increase,{' '}
                <span className="text-green-500">↓ Green</span> = positive decrease.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BehaviorSummary;
